// Markeringen op een schermafbeelding (Optimise): kader, pijl, stift en tekst.
//
// De vormen leven in BEELDPIXELS (de natuurlijke maat van de afbeelding), niet in
// schermpixels: het venster toont de afbeelding verkleind, maar het bestand dat
// meegaat als bijlage wordt op volle grootte getekend. `schaal` vertaalt bij het
// tekenen van beeld- naar doelpixels. Puur en zonder DOM, zodat het te testen is;
// het venster zelf staat in SchermMarkeerVenster.jsx.

export const MARKEER_KLEUREN = [
  { id: 'rood', hex: '#EF4444', label: 'Rood' },
  { id: 'amber', hex: '#F59E0B', label: 'Amber' },
  { id: 'groen', hex: '#22C55E', label: 'Groen' },
  { id: 'blauw', hex: '#3B82F6', label: 'Blauw' },
];

export const MARKEER_GEREEDSCHAP = [
  { id: 'kader', label: 'Kader', uitleg: 'Sleep een rechthoek om iets te omkaderen' },
  { id: 'pijl', label: 'Pijl', uitleg: 'Sleep van start naar punt' },
  { id: 'stift', label: 'Stift', uitleg: 'Teken met de hand (doorschijnend)' },
  { id: 'tekst', label: 'Tekst', uitleg: 'Klik waar de tekst moet komen' },
];

// Lijndikte in beeldpixels, afgestemd op de breedte van de afbeelding: op een
// opname van 1920 px is 5 px goed leesbaar, op een klein plaatje is 2 px genoeg.
export function lijndikteVoor(breedte) {
  return Math.max(2, Math.min(8, Math.round((Number(breedte) || 0) / 400)));
}

// Rechthoek uit twee hoekpunten, in welke richting ook gesleept.
export function normaliseerKader(x1, y1, x2, y2) {
  return { x: Math.min(x1, x2), y: Math.min(y1, y2), b: Math.abs(x2 - x1), h: Math.abs(y2 - y1) };
}

// Een per ongeluk aangeraakt vlak (geen echte sleep, lege tekst) hoort niet in
// de tekening; de aanroeper laat zo'n vorm vallen.
export function isLeeg(vorm, min = 3) {
  if (!vorm) return true;
  switch (vorm.soort) {
    case 'kader': return Math.abs(vorm.x2 - vorm.x1) < min && Math.abs(vorm.y2 - vorm.y1) < min;
    case 'pijl': return Math.hypot(vorm.x2 - vorm.x1, vorm.y2 - vorm.y1) < min;
    case 'stift': return !Array.isArray(vorm.punten) || vorm.punten.length < 2;
    case 'tekst': return !String(vorm.tekst || '').trim();
    default: return true;
  }
}

function tekenPijl(ctx, v, s, dikte) {
  const x1 = v.x1 * s, y1 = v.y1 * s, x2 = v.x2 * s, y2 = v.y2 * s;
  const hoek = Math.atan2(y2 - y1, x2 - x1);
  const kop = Math.max(10, dikte * 4);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  // Pijlpunt: gevulde driehoek op het eindpunt.
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - kop * Math.cos(hoek - Math.PI / 6), y2 - kop * Math.sin(hoek - Math.PI / 6));
  ctx.lineTo(x2 - kop * Math.cos(hoek + Math.PI / 6), y2 - kop * Math.sin(hoek + Math.PI / 6));
  ctx.closePath();
  ctx.fill();
}

// Tekent alle vormen op een 2D-context. `schaal` = doelpixels per beeldpixel;
// `breedte` = breedte van de afbeelding in beeldpixels (voor de lijndikte).
export function tekenVormen(ctx, vormen, { schaal = 1, breedte = 1000 } = {}) {
  const s = schaal;
  const basis = lijndikteVoor(breedte) * s;
  for (const v of vormen || []) {
    if (isLeeg(v)) continue;
    const kleur = v.kleur || MARKEER_KLEUREN[0].hex;
    ctx.save();
    ctx.strokeStyle = kleur;
    ctx.fillStyle = kleur;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalAlpha = 1;
    if (v.soort === 'kader') {
      ctx.lineWidth = basis;
      const k = normaliseerKader(v.x1 * s, v.y1 * s, v.x2 * s, v.y2 * s);
      ctx.strokeRect(k.x, k.y, k.b, k.h);
    } else if (v.soort === 'pijl') {
      ctx.lineWidth = basis;
      tekenPijl(ctx, v, s, basis);
    } else if (v.soort === 'stift') {
      // Markeerstift: dik en doorschijnend, zodat de tekst eronder leesbaar blijft.
      ctx.lineWidth = basis * 3;
      ctx.globalAlpha = 0.4;
      ctx.beginPath();
      v.punten.forEach((p, i) => { if (i === 0) ctx.moveTo(p.x * s, p.y * s); else ctx.lineTo(p.x * s, p.y * s); });
      ctx.stroke();
    } else if (v.soort === 'tekst') {
      const px = Math.round(basis * 4.5);
      ctx.font = `bold ${px}px system-ui, -apple-system, "Segoe UI", sans-serif`;
      ctx.textBaseline = 'top';
      // Witte rand achter de letters houdt ze leesbaar op elke achtergrond.
      ctx.lineWidth = Math.max(2, basis * 0.8);
      ctx.strokeStyle = '#FFFFFF';
      ctx.strokeText(v.tekst, v.x * s, v.y * s);
      ctx.fillText(v.tekst, v.x * s, v.y * s);
    }
    ctx.restore();
  }
}

// Laadt een afbeeldingsbestand als HTMLImageElement (object-URL, daarna opgeruimd).
export function laadAfbeelding(file) {
  return new Promise((res, rej) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); res(img); };
    img.onerror = () => { URL.revokeObjectURL(url); rej(new Error('Kon de afbeelding niet lezen.')); };
    img.src = url;
  });
}

// Bestand met de markeringen erop gebrand, op de volle grootte van de bron, als
// PNG met dezelfde naam. Zonder vormen komt het oorspronkelijke bestand terug.
export async function maakBestandMetMarkering(file, vormen, { laad = laadAfbeelding } = {}) {
  const echte = (vormen || []).filter((v) => !isLeeg(v));
  if (!echte.length) return file;
  const img = await laad(file);
  const b = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const canvas = document.createElement('canvas');
  canvas.width = b; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, b, h);
  tekenVormen(ctx, echte, { schaal: 1, breedte: b });
  const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
  if (!blob) throw new Error('Kon de afbeelding niet opslaan.');
  const naam = String(file.name || 'schermafbeelding.png').replace(/\.(jpe?g|webp|gif|bmp)$/i, '.png');
  // window.File: in App.jsx heet `File` het lucide-icoon; hier is dat niet zo,
  // maar dezelfde schrijfwijze voorkomt die verwarring ooit opnieuw.
  return new window.File([blob], naam, { type: 'image/png' });
}
